import {
  Component, OnInit, Input, OnChanges, ChangeDetectorRef, OnDestroy,
  SimpleChanges, HostListener, ElementRef, ViewChild, AfterViewChecked } from '@angular/core';
import { TimelineRow, TimelineData, TrainLocation, StationInfo } from '../types';
import { BaseComponent } from '../../../../components/general/base/base.component';
import { TrainTimetableEntry, Shift, TrainShift, RunningTrain } from '../../common/train';
import { BehaviorSubject, Subject } from 'rxjs';
import { VRGroupService } from '../../vrgroup.service';
import { takeUntil } from 'rxjs/operators';
import { getTrainLocation, shiftsToTimelineRows, tasksToShiftTaskInfo, shiftToShiftInfo } from './utils';
import { TooltipDirective } from '../../../../components/qm-directives/tooltip-directive/tooltip.directive';
import { TrainDependencyAlarm } from '../../../../alarm/alarm-definitions/train-dependency.alarm';
import { TooltipEvent } from '../../../../components/qm-directives/tooltip-directive/types';
import { LoadingConfig } from '../../../../components/qm-directives/loading.directive';
import {_} from '~shared/utils';

@Component({
  selector: 'qm-timeline-chart',
  templateUrl: './timeline-chart.component.html',
  styleUrls: ['./timeline-chart.component.scss'],
})
export class TimelineChartComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy, AfterViewChecked {
  @ViewChild('chart') private chartElem: ElementRef;
  @ViewChild('indicator') private trainLocationElem: ElementRef;
  @Input()
  public train: RunningTrain;
  @Input()
  public shiftConflictAlarms: TrainDependencyAlarm[];
  public timetableRows: TrainTimetableEntry[];
  public stations: StationInfo[];
  public timelineRows: TimelineRow[] = [];
  public trainShift: TrainShift;
  public personShiftMap = new Map<string, Shift>();
  public columnWidthPx = 55;
  public loading = null;
  public tooltipRect = new BehaviorSubject<DOMRect>(null);
  public loadingConfig: LoadingConfig = {
    messages: {
      empty: _('Personnel work shift information is not available'), // Henkilöstön työvuoroja ei ole saatavilla
    }
  };
  /**
   * trainLocation.columnIndex is an index of grid column where the train currently is.
   * The first station's grid column index is 0. If train is between first and second station
   * then column index index is 1 and so on.
   **/
  public trainLocation: TrainLocation;
  private tooltipOpen = false;
  private scrollToTrainLocation = true;
  private tooltipLoaded = false;

  /**
   * Subscription manager
   * @type {Subject<any>}
   */
  private ngUnsubscribe: Subject<boolean> = new Subject();

  @HostListener('wheel', ['$event'])
  onWheelScroll($event: WheelEvent) {
    const scrollStep = 50;
    if ($event.deltaY > 0) {
      this.chartElem.nativeElement.scrollLeft += scrollStep;
    } else {
      this.chartElem.nativeElement.scrollLeft -= scrollStep;
    }
    this.scrollToTrainLocation = false;
  }

  constructor(
    private vrGroupService: VRGroupService,
    private changeDetector: ChangeDetectorRef) {
    super();
    this.name = 'TimelineChartComponent';
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.train?.currentValue?._id !== changes.train?.previousValue?._id) {
      this.initTimeline();
      this.vrGroupService.makeScheduleRequest(this.train._id);
    }
    if (changes.shiftConflictAlarms) {
      this.updateChart();
    }
  }
  ngOnInit() {
    this.initTimeline();
    this.vrGroupService.trainSchedule$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((data) => {
      if (this.train && data && data.timetable) {
        if (data._id === this.train._id) {
          this.timetableRows = this.updateTimetableRows(this.timetableRows, data.timetable);
          this.updateChart();
          this.changeDetector.markForCheck();
        }
      } else {
        console.warn('No train schedule data available');
      }
    });
  }
  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
  ngAfterViewChecked() {
    if (this.scrollToTrainLocation && this.trainLocationElem && this.chartElem) {
      this.chartElem.nativeElement.scrollLeft =
        this.trainLocationElem.nativeElement.offsetLeft - this.chartElem.nativeElement.clientWidth / 2;
    }
  }
  private updateTimetableRows(oldTimetable: TrainTimetableEntry[], newTimetable: TrainTimetableEntry[]) {
    if (!oldTimetable) {
      return newTimetable;
    } else {
      return oldTimetable.map((row, index) => {
        if (!row.actual && newTimetable[index] && newTimetable[index].actual) {
          row.actual = newTimetable[index].actual;
          row.diff = newTimetable[index].diff;
        }
        return row;
      });
    }
  }

  private initTimeline() {
    if (this.train && this.train.tripNumber && this.train.tripDate) {
      this.vrGroupService.getTrainShift(this.train.tripNumber.toString(), this.train.tripDate)
        .then(serviceAnswer => {
          if (serviceAnswer.isOk && serviceAnswer.data) {
            this.trainShift = serviceAnswer.data;
            return this.vrGroupService.getTrainSchedule(this.train._id);
          }
          return Promise.reject(serviceAnswer);
        })
        .then(serviceAnswer => {
          if (serviceAnswer.isOk && this.trainShift?.shifts?.length && serviceAnswer.data?.data.length) {
            this.timetableRows = serviceAnswer.data?.data[0].timetable;
            this.loading = serviceAnswer;
            this.updateChart();
          } else {
            return Promise.reject(serviceAnswer);
          }

        })
        .catch(err => {
          console.warn('initTimeline failed', err);
          if (err.isOk) {
            /**"No data available" info text */
            this.loading = { isOk: true, data: [] };
          } else {
            /**Error info */
            this.loading = err;
          }
          this.changeDetector.markForCheck();
        });
    } else {
      console.warn('No running train data available', this.train);
      /**"No data available" info text */
      this.loading = { isOk: true, data: [] };
    }
  }

  /**Update timeline chart datas */
  private updateChart(): void {
    if (this.timetableRows && this.trainShift && !this.tooltipOpen) {
      const trainStopRows = this.timetableRows.filter(row => row.stop);
      this.trainLocation = getTrainLocation(trainStopRows);
      this.stations = trainStopRows
        .filter((row, index) => index === 0 || row.type === 'ARRIVAL')
        .map((row, index) => ({
          shortCode: row.station,
          passed: this.trainLocation.columnIndex > index * 2,
        }));

      this.trainShift.shifts.forEach(shift => {
        this.personShiftMap.set(shift.personId, shift);
      });
      this.timelineRows = shiftsToTimelineRows(
        this.trainShift.trainNumber,
        this.trainShift.shifts,
        this.stations.map(s => s.shortCode),
        this.shiftConflictAlarms);
      /**Don't show empty rows */
      this.timelineRows = this.timelineRows.filter(row => row.datas.length);
      this.loading = false;
      this.changeDetector.markForCheck();
    }
  }
  toggleTooltip(tooltipEvent: TooltipEvent, tooltip: TooltipDirective, rowData: TimelineData) {
    const shift = this.personShiftMap.get(rowData.label);
    let validTasks = shift.tasks.filter(task => task.role && task.fromStation !== task.toStation);
    const trainIndex = validTasks.findIndex(task => task.trainNumber === this.train.tripNumber.toString());
    /**Dont show previous trains' tasks */
    validTasks = validTasks.splice(trainIndex);
    if (tooltipEvent.isOpen) {
      this.tooltipOpen = false;
      this.tooltipLoaded = false;
      this.tooltipRect.next(null);
    } else {
      this.tooltipOpen = true;
      this.tooltipLoaded = false;
      tooltip.open(
        {
          shiftInfo: shiftToShiftInfo(shift),
          shiftTaskInfo: tasksToShiftTaskInfo(validTasks, this.shiftConflictAlarms),
          event: tooltipEvent.event
        });
    }
  }
  onLoaded($event: DOMRect) {
    if (!this.tooltipLoaded) {
      this.tooltipLoaded = true;
      this.tooltipRect.next($event);
      this.changeDetector.detectChanges();
    }
  }
}
